// --- React · 라우팅 ---
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 ---
import { useTheme } from '@emotion/react';
import type { AppTheme } from '@/theme/theme';
import { createAuthStyles } from '@/components/auth/authStyles';
import registerImage from '/images/register.png';

// --- 공통 UI 컴포넌트 ---
import { Input } from '@/components/ui/common/input/Input';
import { LanguageTabs } from '@/components/ui/common/LanguageTabs';
import { Button } from '@/components/ui';

// --- API ---
import { apiHelpers, extractApiData } from '@/api/core';

// --- 유효성 검사 · 에러 파싱 ---
import { parseApiError } from '@/utils/errors';

// --- 스토어 ---
import { useNotificationStore } from '@/store/notificationStore';

// --- 타입 ---
import type {
  ValidateResetTokenRequest,
  ValidateResetTokenResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from '@packages/types/auth/dto';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// ==================== 컴포넌트 ====================

const ResetPassword: React.FC = () => {
  const theme = useTheme() as AppTheme;
  const { t, i18n } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotificationStore();

  // ==================== 상태 관리 ====================

  // 폼 데이터
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // 로딩 · 토큰 검증 상태
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);

  // URL 파라미터에서 추출한 토큰 · 이메일
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // ==================== 핸들러 ====================

  /** 언어 변경 */
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  // ==================== 이펙트 ====================

  /**
   * 페이지 진입 시 리셋 토큰 유효성 검증.
   * token 또는 email이 없으면 에러 표시,
   * 유효하지 않으면 만료/무효 안내 화면으로 전환.
   */
  useEffect(() => {
    const validateToken = async () => {
      if (!token || !email) {
        showError('auth:invalid_link', 'auth:missing_token_or_email');
        setIsValidating(false);
        return;
      }

      try {
        const validateData: ValidateResetTokenRequest = {
          email: decodeURIComponent(email),
          token,
        };
        const response = await apiHelpers.post<ValidateResetTokenResponse>(
          'auth.validateResetToken',
          validateData
        );

        if (extractApiData(response)?.valid) {
          setIsValidToken(true);
        } else {
          showError('auth:invalid_token', 'auth:token_expired_or_invalid');
        }
      } catch (error) {
        showError('auth:invalid_token', 'auth:token_expired_or_invalid');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, email, showError]);

  /**
   * 비밀번호 재설정 폼 제출.
   * 흐름:
   *   1) 비밀번호 일치 확인
   *   2) 비밀번호 최소 길이 검증
   *   3) token · email 존재 여부 확인
   *   4) API 호출 → 성공 시 로그인 페이지 이동
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 1) 중복 제출 방지
    if (isLoading) {
      return;
    }

    // 2) 비밀번호 일치 확인
    if (newPassword !== confirmPassword) {
      showError('auth:password_mismatch', 'auth:passwords_do_not_match');
      return;
    }

    // 3) 비밀번호 최소 길이 검증
    if (newPassword.length < 8) {
      showError('auth:password_too_short', 'auth:password_minimum_8_characters');
      return;
    }

    // 4) token · email 존재 여부 확인
    if (!token || !email) {
      showError('auth:invalid_link', 'auth:missing_token_or_email');
      return;
    }

    setIsLoading(true);

    try {
      // 5) API 호출
      const resetData: ResetPasswordRequest = {
        email: decodeURIComponent(email),
        token,
        newPassword,
      };
      const response = await apiHelpers.post<ResetPasswordResponse>(
        'auth.resetPassword',
        resetData
      );

      if (response.data.success) {
        showSuccess('auth:password_reset_success', 'auth:you_can_now_login');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error: unknown) {
      const e = parseApiError(error);
      showError(e.title, e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== 렌더링 ====================

  const styles = createAuthStyles(registerImage, theme);

  // 토큰 검증 중 — 로딩 화면
  if (isValidating) {
    return (
      <div css={styles.container}>
        {/* 왼쪽 섹션 - 브랜드 이미지 (데스크톱 전용) */}
        <div css={styles.leftSection}>
          <div css={styles.leftOverlay} />
          <div css={styles.leftContent}>
            <div css={styles.leftText}>
              <img src="/images/bellcloud.svg" alt="BellCloud Logo" css={styles.logo} />
            </div>
          </div>
        </div>

        {/* 오른쪽 섹션 - 검증 중 안내 */}
        <div css={styles.rightSection}>
          <div css={styles.formContainer}>
            <div css={styles.contentAreaCentered}>
              <p css={styles.messageTextSecondary}>{t(i18nKeys.auth.validating_token)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 토큰 무효 — 에러 안내 화면
  if (!isValidToken) {
    return (
      <div css={styles.container}>
        {/* 왼쪽 섹션 - 브랜드 이미지 (데스크톱 전용) */}
        <div css={styles.leftSection}>
          <div css={styles.leftOverlay} />
          <div css={styles.leftContent}>
            <div css={styles.leftText}>
              <img src="/images/bellcloud.svg" alt="BellCloud Logo" css={styles.logo} />
            </div>
          </div>
        </div>

        {/* 오른쪽 섹션 - 토큰 무효 안내 */}
        <div css={styles.rightSection}>
          <div css={styles.formContainer}>
            <div css={styles.contentAreaCentered}>
              <div css={styles.iconLarge}>⚠️</div>
              <h2 css={styles.formTitle}>{t(i18nKeys.auth.invalid_token)}</h2>
              <p css={styles.messageTextSecondary}>{t(i18nKeys.auth.token_expired_or_invalid)}</p>
              {/* 새 링크 요청 버튼 */}
              <Button
                type="button"
                variant="primary"
                width="100%"
                onClick={() => navigate('/forgot-password')}
              >
                {t(i18nKeys.auth.request_new_link)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 기본 화면 — 새 비밀번호 입력 폼
  return (
    <div css={styles.container}>
      {/* 왼쪽 섹션 - 브랜드 이미지 (데스크톱 전용) */}
      <div css={styles.leftSection}>
        <div css={styles.leftOverlay} />
        <div css={styles.leftContent}>
          <div css={styles.leftText}>
            <img src="/images/bellcloud.svg" alt="BellCloud Logo" css={styles.logo} />
          </div>
        </div>
      </div>

      {/* 오른쪽 섹션 - 비밀번호 재설정 폼 */}
      <div css={styles.rightSection}>
        <div css={styles.formContainer}>
          {/* 타이틀 + 언어 선택 */}
          <div css={styles.header}>
            <div>
              <h2 css={styles.formTitle}>{t(i18nKeys.auth.reset_password)}</h2>
              <div css={styles.formTitleLine} />
            </div>

            <div css={styles.headerLanguageTabs}>
              <LanguageTabs
                currentLanguage={i18n.language}
                onLanguageChange={handleLanguageChange}
              />
            </div>
          </div>

          <p css={styles.description}>{t(i18nKeys.auth.enter_new_password)}</p>

          {/* 비밀번호 재설정 폼 */}
          <form css={styles.form} onSubmit={handleSubmit}>
            <div css={styles.fieldGroup}>
              {/* 새 비밀번호 필드 */}
              <div css={styles.field}>
                <p>{t(i18nKeys.auth.new_password)} *</p>
                <Input
                  type="password"
                  name="newPassword"
                  size="lg"
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder={t(i18nKeys.auth.new_password)}
                  fullWidth
                />
              </div>
              {/* 비밀번호 확인 필드 */}
              <div css={styles.field}>
                <p>{t(i18nKeys.auth.confirm_password)} *</p>
                <Input
                  type="password"
                  name="confirmPassword"
                  size="lg"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder={t(i18nKeys.auth.confirm_password)}
                  fullWidth
                />
              </div>
            </div>

            {/* 재설정 버튼 */}
            <Button type="submit" variant="primary" disabled={isLoading} width="100%">
              {isLoading ? t(i18nKeys.auth.resetting) : t(i18nKeys.auth.reset_password)}
            </Button>
          </form>

          {/* 하단 링크 (로그인 페이지로 이동) */}
          <div css={styles.bottomLinks}>
            <p css={styles.linkText}>
              {t(i18nKeys.auth.remember_password)}{' '}
              <Link to="/login" css={styles.link}>
                {t(i18nKeys.auth.back_to_login)}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
